import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  motion,
  useInView,
  useScroll,
  useTransform,
  AnimatePresence,
} from "framer-motion";
import { ArrowRight, ExternalLink, Code2, Layers, Zap, Smartphone, Server, Globe, MessageSquare, CheckCircle2 } from "lucide-react";
import Navbar from "./Navbar";
import { events } from '../utils/analytics';
import { PageSEO, serviceSchema } from '../utils/seo';
import LoadingScreen from "./LoadingScreen";
import FlowingLines from "./FlowingLines";

/* ─── Dati ─── */
const STACK = [
  "React",
  "React Native",
  "Node.js",
  "Firebase",
  "Supabase",
  "TypeScript",
];

const PROGETTI = [
  {
    slug: "veyro",
    nome: "Veyro - Allenamento & PT",
    tagline: "Un'app, ogni trainer",
    status: "sviluppo",
    stack: ["React Native", "Supabase"],
    href: "/veyro",
    color: "#10b981",
  },
  {
    slug: "veyro-trainer",
    nome: "Veyro Trainer - App per PT",
    tagline: "Il tuo studio, in tasca",
    status: "sviluppo",
    stack: ["React Native", "Stripe Connect"],
    href: "/veyro-trainer",
    color: "#16914c",
  },
  {
    slug: "accademiacampania",
    nome: "Accademia Campania",
    tagline: "Piattaforma Élite per la danza sportiva",
    status: "live",
    stack: ["Next.js", "Supabase"],
    href: "/progetti/accademia-campania",
    color: "#d4af37",
  },
  {
    slug: "crewboat",
    nome: "CrewBoat",
    tagline: "Gestione equipaggio e turni per flotte nautiche",
    status: "sviluppo",
    stack: ["React Native", "Firebase"],
    href: "/crewboat",
    color: "#0ea5e9",
  },
  {
    slug: "ipairly",
    nome: "iPairly",
    tagline: "Giochi interattivi per coppie e gruppi",
    status: "sviluppo",
    stack: ["React Native", "Supabase"],
    href: "/ipairly",
    color: "#f472b6",
  },
];

const STATUS_CONFIG = {
  live: { label: "Live", color: "#34d399", bg: "rgba(52,211,153,0.1)" },
  sviluppo: {
    label: "In sviluppo",
    color: "#fbbf24",
    bg: "rgba(251,191,36,0.1)",
  },
  concept: { label: "Concept", color: "#22d3ee", bg: "rgba(34,211,238,0.1)" },
};

/* ─── Utility: reveal generico ─── */
function Reveal({ children, delay = 0, y = 28 }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-60px" });
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.75, ease: [0.16, 1, 0.3, 1], delay }}
    >
      {children}
    </motion.div>
  );
}

/* ─── Riga dello scroll-stack ─── */
function StackRow({
  word,
  scrollYProgress,
  startOpacity,
  endOpacity,
  fontSize,
  fontWeight,
  translateX,
  gradient,
  color,
}) {
  const opacity = useTransform(
    scrollYProgress,
    [0, 0.65],
    [startOpacity, endOpacity],
  );
  const x = useTransform(scrollYProgress, [0, 1], [translateX, 0]);

  const textStyle = gradient
    ? {
        background: gradient,
        WebkitBackgroundClip: "text",
        WebkitTextFillColor: "transparent",
        backgroundClip: "text",
      }
    : { color: color || "white" };

  return (
    <div style={{ overflow: "hidden", lineHeight: 1.05 }}>
      <motion.div style={{ opacity, x }}>
        <span
          style={{
            fontFamily: "var(--font-display)",
            fontSize,
            fontWeight,
            letterSpacing: "-0.04em",
            display: "block",
            textAlign: "center",
            ...textStyle,
          }}
        >
          {word}
        </span>
      </motion.div>
    </div>
  );
}

/* ─── Orb CSS ─── */
function Orb() {
  return (
    <div
      style={{
        position: "absolute",
        top: "50%",
        left: "50%",
        transform: "translate(-50%, -50%)",
        width: "clamp(180px, 22vw, 340px)",
        height: "clamp(180px, 22vw, 340px)",
        borderRadius: "50%",
        background: `
        radial-gradient(circle at 32% 30%, rgba(255,255,255,0.22) 0%, transparent 45%),
        radial-gradient(circle at 70% 65%, rgba(244,114,182,0.6) 0%, rgba(124,58,237,0.8) 35%, rgba(34,211,238,0.3) 65%, transparent 80%)
      `,
        boxShadow: `
        0 0 60px rgba(124,58,237,0.55),
        0 0 120px rgba(124,58,237,0.30),
        0 0 200px rgba(244,114,182,0.18),
        inset 0 0 40px rgba(255,255,255,0.06)
      `,
        animation:
          "orbFloat 7s ease-in-out infinite alternate, orbSpin 18s linear infinite",
        zIndex: 2,
        pointerEvents: "none",
      }}
    />
  );
}

/* ─── Status badge ─── */
function StatusBadge({ status }) {
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.concept;
  return (
    <span
      style={{
        fontFamily: "var(--font-mono)",
        fontSize: "0.68rem",
        fontWeight: 500,
        background: cfg.bg,
        color: cfg.color,
        border: `1px solid ${cfg.color}50`,
        borderRadius: "var(--radius-sm)",
        padding: "2px 9px",
        letterSpacing: "0.05em",
      }}
    >
      {cfg.label}
    </span>
  );
}

/* ─── Project card ─── */
function ProjectCard({ p, delay }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-50px" });
  const [hov, setHov] = useState(false);

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 36 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.65, ease: [0.16, 1, 0.3, 1], delay }}
    >
      <Link
        to={p.href}
        target={p.href.startsWith("http") ? "_blank" : undefined}
        rel="noopener noreferrer"
        onMouseEnter={() => setHov(true)}
        onMouseLeave={() => setHov(false)}
        style={{
          display: "block",
          background: hov
            ? "rgba(255,255,255,0.055)"
            : "rgba(255,255,255,0.025)",
          border: `1px solid ${hov ? `${p.color}55` : "rgba(255,255,255,0.07)"}`,
          backdropFilter: "blur(20px)",
          WebkitBackdropFilter: "blur(20px)",
          borderRadius: "var(--radius-lg)",
          padding: "1.75rem",
          transition: "all 0.32s cubic-bezier(0.4,0,0.2,1)",
          transform: hov ? "translateY(-7px)" : "translateY(0)",
          boxShadow: hov
            ? `0 24px 60px ${p.color}22, 0 0 0 1px ${p.color}22`
            : "0 4px 24px rgba(0,0,0,0.28)",
          textDecoration: "none",
          position: "relative",
          overflow: "hidden",
        }}
      >
        <div
          style={{
            position: "absolute",
            top: 0,
            right: 0,
            width: 130,
            height: 130,
            background: `radial-gradient(circle at top right, ${p.color}18, transparent 70%)`,
            transition: "opacity 0.32s",
            opacity: hov ? 1 : 0.5,
            pointerEvents: "none",
          }}
        />

        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            marginBottom: "1.1rem",
          }}
        >
          <div
            style={{
              width: 46,
              height: 46,
              borderRadius: "12px",
              background: `linear-gradient(135deg, ${p.color}28, ${p.color}10)`,
              border: `1px solid ${p.color}30`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontFamily: "var(--font-mono)",
              fontWeight: 700,
              fontSize: "0.85rem",
              color: p.color,
            }}
          >
            {p.nome.slice(0, 2).toUpperCase()}
          </div>
          <StatusBadge status={p.status} />
        </div>

        <h3
          style={{
            fontFamily: "var(--font-display)",
            fontWeight: 700,
            fontSize: "1.25rem",
            color: "var(--color-text)",
            marginBottom: "0.4rem",
            letterSpacing: "-0.02em",
          }}
        >
          {p.nome}
        </h3>
        <p
          style={{
            fontSize: "1rem",
            color: "var(--color-muted)",
            marginBottom: "1.25rem",
            lineHeight: 1.55,
          }}
        >
          {p.tagline}
        </p>
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: "6px",
            marginBottom: "1.5rem",
          }}
        >
          {p.stack.map((t) => (
            <span key={t} className="stack-badge">
              {t}
            </span>
          ))}
        </div>
        <span
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: "6px",
            fontFamily: "var(--font-body)",
            fontWeight: 600,
            fontSize: "0.85rem",
            color: hov ? p.color : "var(--color-muted)",
            transition: "color 0.3s",
          }}
        >
          Scopri <ArrowRight size={13} />
        </span>
      </Link>
    </motion.div>
  );
}

/* ═══════════════════════════════════════════════
   SCROLL-STACK SECTION
   Tre parole raccontano il processo: IDEA → PRODOTTO → LANCIO
═══════════════════════════════════════════════ */
function ScrollStackSection() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });

  return (
    <section
      ref={ref}
      style={{
        borderTop: "1px solid var(--color-border)",
        padding: "2rem 0 1rem",
        overflow: "hidden",
        position: "relative",
      }}
    >
      {/* Riga 1 — PENSA: thin, il momento iniziale */}
      <StackRow
        word="PENSA"
        scrollYProgress={scrollYProgress}
        startOpacity={0.05}
        endOpacity={0.16}
        fontSize="clamp(4rem, 10vw, 12rem)"
        fontWeight={200}
        color="rgba(255,255,255,0.90)"
        translateX={-50}
      />
      {/* Riga 2 — COSTRUISCI: medium, gradiente — il processo */}
      <StackRow
        word="COSTRUISCI"
        scrollYProgress={scrollYProgress}
        startOpacity={0.08}
        endOpacity={0.32}
        fontSize="clamp(3rem, 7.5vw, 9rem)"
        fontWeight={700}
        gradient="linear-gradient(135deg, #c4b5fd 0%, #f472b6 100%)"
        translateX={40}
      />
      {/* Riga 3 — LANCIA: heavy, cyan — il risultato */}
      <StackRow
        word="LANCIA"
        scrollYProgress={scrollYProgress}
        startOpacity={0.14}
        endOpacity={0.72}
        fontSize="clamp(4rem, 10vw, 12rem)"
        fontWeight={900}
        color="#22d3ee"
        translateX={-25}
      />
    </section>
  );
}

/* ═══════════════════════════════════════════════
   FLOWING LINES SECTION
═══════════════════════════════════════════════ */
function FlowingLinesSection() {
  return (
    <section
      style={{
        borderTop: "1px solid var(--color-border)",
        position: "relative",
        overflow: "hidden",
        minHeight: "65vh",
        display: "flex",
        alignItems: "center",
      }}
    >
      <FlowingLines />

      {/* Vignette top/bottom — più spessa per leggibilità testo */}
      <div
        style={{
          position: "absolute",
          inset: 0,
          pointerEvents: "none",
          background:
            "linear-gradient(to bottom, var(--color-bg) 0%, rgba(5,5,10,0.18) 20%, transparent 38%, transparent 62%, rgba(5,5,10,0.18) 80%, var(--color-bg) 100%)",
          zIndex: 1,
        }}
      />

      {/* Testo sovrapposto */}
      <div
        className="container"
        style={{
          position: "relative",
          zIndex: 2,
          textAlign: "center",
          padding: "4rem 1.75rem",
        }}
      >
        <Reveal>
          <span
            style={{
              fontFamily: "var(--font-mono)",
              fontSize: "0.85rem",
              color: "var(--color-accent-mid)",
              letterSpacing: "0.12em",
              textTransform: "uppercase",
              display: "block",
              marginBottom: "1.5rem",
            }}
          >
            // approccio
          </span>

          <h2
            style={{
              fontFamily: "var(--font-display)",
              fontSize: "clamp(2.6rem, 5.5vw, 4.2rem)",
              lineHeight: 1.12,
              letterSpacing: "-0.03em",
              color: "#ffffff",
              textShadow: "0 2px 40px rgba(0,0,0,0.8)",
            }}
          >
            Dal problema al prodotto, <br />
            <span
              style={{
                fontFamily: "var(--font-display)",
                fontWeight: 800,
                background: "var(--gradient-text)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                backgroundClip: "text",
              }}
            >
              senza scorciatoie
            </span>
          </h2>

          <p
            style={{
              color: "rgba(255,255,255,0.82)",
              fontSize: "1.1rem",
              fontWeight: 400,
              maxWidth: "44ch",
              margin: "1.75rem auto 0",
              lineHeight: 1.75,
              textShadow: "0 1px 20px rgba(0,0,0,0.9)",
            }}
          >
            Ogni progetto parte dal problema reale, non dalla tecnologia. Stack
            scelto per durare, architettura pensata per crescere.
          </p>
        </Reveal>
      </div>
    </section>
  );
}

/* ═══════════════════════════════════════════════
   HOMEPAGE
═══════════════════════════════════════════════ */
export default function HomePage() {
  const [loaded, setLoaded] = useState(() => sessionStorage.getItem('vmd_splash_seen') === '1');

  const handleSplashComplete = () => {
    sessionStorage.setItem('vmd_splash_seen', '1');
    setLoaded(true);
  };

  /* Smooth scroll Lenis */
  useEffect(() => {
    if (!loaded) return;
    let lenis;
    import("lenis").then(({ default: Lenis }) => {
      lenis = new Lenis({
        duration: 1.3,
        easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      });
      const raf = (time) => {
        lenis.raf(time);
        requestAnimationFrame(raf);
      };
      requestAnimationFrame(raf);
    });
    return () => lenis?.destroy();
  }, [loaded]);

  return (
    <>
      {/* Preloader */}
      <AnimatePresence>
        {!loaded && <LoadingScreen onComplete={handleSplashComplete} />}
      </AnimatePresence>

      {loaded && (
        <>
          <PageSEO
            description="VMDProject — sviluppo app mobile iOS e Android, SaaS B2B, web app e consulenza tecnica. Trasformo idee in prodotti digitali completi, dall'MVP al lancio."
            canonical="/"
            jsonLd={serviceSchema([
              { name: 'App Mobile iOS & Android', description: 'Sviluppo app cross-platform con React Native per iOS e Android, dalla progettazione alla pubblicazione sugli store.' },
              { name: 'SaaS B2B', description: 'Piattaforme SaaS verticali con dashboard multi-tenant, gestione ruoli, pagamenti Stripe e API REST.' },
              { name: 'Web App', description: 'Single Page Application React ottimizzate per SEO, con integrazione database, analytics e deploy cloud.' },
              { name: 'Consulenza Tecnica', description: 'Architettura di sistema, code review, scelta dello stack tecnologico e MVP planning.' },
            ])}
          />
          <Navbar />

          <motion.main
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.4 }}
          >
            {/* ══════════ HERO ══════════ */}
            <section
              style={{
                minHeight: "100vh",
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                position: "relative",
                overflow: "hidden",
              }}
            >
              {/* Orb centrale */}
              <Orb />

              {/* Blob di sfondo */}
              <div
                className="blob"
                style={{
                  width: 700,
                  height: 700,
                  background:
                    "radial-gradient(circle, rgba(124,58,237,0.20) 0%, transparent 65%)",
                  top: "-15%",
                  left: "-10%",
                  animationDuration: "16s",
                }}
              />
              <div
                className="blob"
                style={{
                  width: 500,
                  height: 500,
                  background:
                    "radial-gradient(circle, rgba(244,114,182,0.15) 0%, transparent 65%)",
                  bottom: "5%",
                  right: "-8%",
                  animationDuration: "20s",
                  animationDelay: "-8s",
                }}
              />

              {/* Testo gigante — 3 righe */}
              <div
                style={{
                  position: "relative",
                  zIndex: 3,
                  padding: "80px 1.25rem 0",
                  textAlign: "center",
                }}
              >
                {/* Pill stato */}
                <motion.div
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{
                    duration: 0.55,
                    delay: 0.1,
                    ease: [0.16, 1, 0.3, 1],
                  }}
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "9px",
                    background: "rgba(255,255,255,0.04)",
                    border: "1px solid rgba(255,255,255,0.09)",
                    backdropFilter: "blur(12px)",
                    borderRadius: "100px",
                    padding: "7px 16px",
                    marginBottom: "3rem",
                  }}
                >
                  <span
                    style={{
                      width: 7,
                      height: 7,
                      borderRadius: "50%",
                      background: "#34d399",
                      boxShadow: "0 0 8px #34d399",
                      animation: "dotPulse 2.5s ease infinite",
                      flexShrink: 0,
                    }}
                  />
                  <span
                    style={{
                      fontFamily: "var(--font-mono)",
                      fontSize: "0.9rem",
                      color: "var(--color-muted)",
                      letterSpacing: "0.03em",
                    }}
                  >
                    Developer · Builder di SaaS · Aperto a collaborazioni
                  </span>
                </motion.div>

                <h1 style={{ margin: 0, fontSize: "inherit", fontWeight: "inherit", lineHeight: "inherit", letterSpacing: "inherit" }}>
                {/* Riga 1: DEVELOPER — ultra thin */}
                <motion.span
                  style={{ display: "block" }}
                  initial={{ opacity: 0, y: 40 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{
                    duration: 0.75,
                    ease: [0.16, 1, 0.3, 1],
                    delay: 0.2,
                  }}
                >
                  <span
                    style={{
                      fontFamily: "var(--font-display)",
                      fontWeight: 200,
                      fontSize: "clamp(3rem, 13vw, 18rem)",
                      letterSpacing: "-0.04em",
                      lineHeight: 0.95,
                      color: "rgba(255,255,255,0.92)",
                      display: "block",
                    }}
                  >
                    DEVELOPER
                  </span>
                </motion.span>

                {/* Riga 2: & BUILDER — bold, gradiente */}
                <motion.span
                  style={{ display: "block" }}
                  initial={{ opacity: 0, y: 48 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{
                    duration: 0.75,
                    ease: [0.16, 1, 0.3, 1],
                    delay: 0.32,
                  }}
                >
                  <span
                    style={{
                      fontFamily: "var(--font-display)",
                      fontWeight: 900,
                      fontSize: "clamp(3rem, 13vw, 18rem)",
                      letterSpacing: "-0.04em",
                      lineHeight: 0.95,
                      background:
                        "linear-gradient(135deg, #c4b5fd 0%, #f472b6 100%)",
                      WebkitBackgroundClip: "text",
                      WebkitTextFillColor: "transparent",
                      backgroundClip: "text",
                      display: "block",
                    }}
                  >
                    & BUILDER
                  </span>
                </motion.span>

                {/* Riga 3: DI SAAS — bold, cyan */}
                <motion.span
                  style={{ display: "block" }}
                  initial={{ opacity: 0, y: 56 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{
                    duration: 0.75,
                    ease: [0.16, 1, 0.3, 1],
                    delay: 0.44,
                  }}
                >
                  <span
                    style={{
                      fontFamily: "var(--font-display)",
                      fontWeight: 800,
                      fontSize: "clamp(3rem, 13vw, 18rem)",
                      letterSpacing: "-0.04em",
                      lineHeight: 0.95,
                      color: "#22d3ee",
                      display: "block",
                    }}
                  >
                    DI SaaS
                    <span
                      className="cursor-blink"
                      style={{
                        fontFamily: "var(--font-mono)",
                        fontSize: "0.6em",
                        marginLeft: "6px",
                        color: "var(--color-accent-mid)",
                      }}
                    />
                  </span>
                </motion.span>
                </h1>
              </div>

              {/* Tagline + CTA sotto il testo */}
              <div
                style={{
                  position: "relative",
                  zIndex: 3,
                  textAlign: "center",
                  marginTop: "4rem",
                  padding: "0 1.25rem 5rem",
                }}
              >
                <motion.p
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.7, delay: 0.65 }}
                  style={{
                    fontSize: "clamp(1.15rem, 2vw, 1.45rem)",
                    color: "var(--color-text-2)",
                    fontWeight: 300,
                    maxWidth: "52ch",
                    margin: "0 auto 2.5rem",
                    lineHeight: 1.8,
                  }}
                >
                  Costruisco applicazioni web e mobile end-to-end — dall'idea al
                  lancio. Se hai un progetto,{" "}
                  <span
                    style={{
                      fontFamily: "var(--font-display)",
                      fontWeight: 800,
                      background: "var(--gradient-text)",
                      WebkitBackgroundClip: "text",
                      WebkitTextFillColor: "transparent",
                      backgroundClip: "text",
                    }}
                  >
                    costruiamolo insieme.
                  </span>
                </motion.p>

                <motion.div
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{
                    duration: 0.6,
                    delay: 0.78,
                    ease: [0.16, 1, 0.3, 1],
                  }}
                  style={{
                    display: "flex",
                    gap: "1rem",
                    justifyContent: "center",
                    flexWrap: "wrap",
                  }}
                >
                  <Link to="/progetti" className="btn-primary" onClick={events.ctaProgetti}>
                    Esplora i Progetti <ArrowRight size={15} />
                  </Link>
                  <Link to="/collabora" className="btn-secondary" onClick={events.ctaCollabora}>
                    Collaboriamo
                  </Link>
                </motion.div>
              </div>
            </section>

            {/* ══════════ SCROLL-STACKED TEXT ══════════ */}
            <ScrollStackSection />

            {/* ══════════ FLOWING LINES ══════════ */}
            <FlowingLinesSection />

            {/* ══════════ CHI SONO ══════════ */}
            <section
              className="section"
              style={{ borderTop: "1px solid var(--color-border)" }}
            >
              <div className="container">
                <Reveal>
                  <span
                    style={{
                      fontFamily: "var(--font-mono)",
                      fontSize: "0.85rem",
                      color: "var(--color-accent-mid)",
                      letterSpacing: "0.12em",
                      textTransform: "uppercase",
                      display: "block",
                      marginBottom: "1.1rem",
                    }}
                  >
                    // chi sono
                  </span>
                  <h2
                    style={{
                      fontFamily: "var(--font-display)",
                      fontSize: "clamp(2rem, 4vw, 2.8rem)",
                      maxWidth: "28ch",
                      lineHeight: 1.15,
                    }}
                  >
                    Un developer che{" "}
                    <span
                      style={{
                        fontFamily: "var(--font-display)",
                        fontWeight: 800,
                        background: "var(--gradient-text)",
                        WebkitBackgroundClip: "text",
                        WebkitTextFillColor: "transparent",
                        backgroundClip: "text",
                      }}
                    >
                      non si ferma al codice
                    </span>
                  </h2>
                  <p
                    style={{
                      color: "var(--color-text-2)",
                      fontSize: "1.15rem",
                      maxWidth: "56ch",
                      lineHeight: 1.8,
                      marginTop: "1.1rem",
                      fontWeight: 300,
                    }}
                  >
                    Sono Vincenzo Di Pace, sviluppatore freelance con base a
                    Napoli. Con VMDProject progetto, sviluppo e lancio app
                    mobile, SaaS e web app — dal backend alle interfacce. Ogni
                    progetto è un sistema pensato per crescere.
                  </p>
                </Reveal>

                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fit, minmax(270px, 1fr))",
                    gap: "1.5rem",
                    marginTop: "3rem",
                  }}
                >
                  {[
                    {
                      icon: Code2,
                      titolo: "Full-Stack",
                      color: "#7c3aed",
                      testo:
                        "Dal backend alle interfacce mobile. Costruisco prodotti completi, non singole feature.",
                    },
                    {
                      icon: Layers,
                      titolo: "SaaS Builder",
                      color: "#f472b6",
                      testo:
                        "Progetto applicazioni verticali pensate per scalare e generare valore reale nel tempo.",
                    },
                    {
                      icon: Zap,
                      titolo: "Velocità",
                      color: "#22d3ee",
                      testo:
                        "MVP in settimane, non mesi. Iterazione rapida senza sacrificare la qualità finale.",
                    },
                  ].map(({ icon: Icon, titolo, color, testo }, i) => (
                    <Reveal key={titolo} delay={i * 0.1}>
                      <div
                        onMouseEnter={(e) => {
                          e.currentTarget.style.border = `1px solid ${color}55`;
                          e.currentTarget.style.boxShadow = `0 20px 60px ${color}18, inset 0 1px 0 ${color}15`;
                          e.currentTarget.style.transform = "translateY(-6px)";
                          e.currentTarget.style.background = `rgba(255,255,255,0.05)`;
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.border = `1px solid ${color}28`;
                          e.currentTarget.style.boxShadow = `0 4px 24px rgba(0,0,0,0.2)`;
                          e.currentTarget.style.transform = "translateY(0)";
                          e.currentTarget.style.background =
                            "rgba(255,255,255,0.025)";
                        }}
                        style={{
                          background: "rgba(255,255,255,0.025)",
                          border: `1px solid ${color}28`,
                          backdropFilter: "blur(16px)",
                          borderRadius: "var(--radius-lg)",
                          padding: "2rem",
                          position: "relative",
                          overflow: "hidden",
                          transition: "all 0.32s cubic-bezier(0.4,0,0.2,1)",
                          boxShadow: "0 4px 24px rgba(0,0,0,0.2)",
                        }}
                      >
                        {/* Glow angolo */}
                        <div
                          style={{
                            position: "absolute",
                            top: 0,
                            right: 0,
                            width: 180,
                            height: 180,
                            background: `radial-gradient(circle at top right, ${color}20, transparent 65%)`,
                            pointerEvents: "none",
                          }}
                        />
                        {/* Accent line in basso */}
                        <div
                          style={{
                            position: "absolute",
                            bottom: 0,
                            left: 0,
                            right: 0,
                            height: "2px",
                            background: `linear-gradient(90deg, ${color}70, ${color}00)`,
                            pointerEvents: "none",
                          }}
                        />
                        {/* Icona */}
                        <div
                          style={{
                            width: 52,
                            height: 52,
                            borderRadius: "14px",
                            background: `${color}15`,
                            border: `1px solid ${color}35`,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            marginBottom: "1.4rem",
                            boxShadow: `0 0 20px ${color}25`,
                          }}
                        >
                          <Icon size={22} color={color} />
                        </div>
                        <h3
                          style={{
                            fontFamily: "var(--font-display)",
                            fontWeight: 700,
                            fontSize: "1.2rem",
                            marginBottom: "0.6rem",
                          }}
                        >
                          {titolo}
                        </h3>
                        <p
                          style={{
                            color: "var(--color-muted)",
                            fontSize: "1rem",
                            lineHeight: 1.7,
                          }}
                        >
                          {testo}
                        </p>
                      </div>
                    </Reveal>
                  ))}
                </div>

                <Reveal delay={0.25}>
                  <div
                    style={{
                      marginTop: "2.5rem",
                      display: "flex",
                      flexWrap: "wrap",
                      gap: "8px",
                      alignItems: "center",
                    }}
                  >
                    <span
                      style={{
                        fontFamily: "var(--font-mono)",
                        fontSize: "0.9rem",
                        color: "var(--color-muted)",
                        marginRight: "4px",
                      }}
                    >
                      stack →
                    </span>
                    {STACK.map((t) => (
                      <span key={t} className="stack-badge">
                        {t}
                      </span>
                    ))}
                  </div>
                </Reveal>
              </div>
            </section>

            {/* ══════════ SERVIZI ══════════ */}
            <section
              className="section"
              style={{
                borderTop: "1px solid var(--color-border)",
                position: "relative",
                overflow: "hidden",
              }}
            >
              {/* blob decorativo */}
              <div
                className="blob"
                style={{
                  width: 600,
                  height: 500,
                  background:
                    "radial-gradient(circle, rgba(124,58,237,0.1) 0%, transparent 70%)",
                  top: "-10%",
                  left: "-20%",
                  pointerEvents: "none",
                }}
              />
              <div className="container" style={{ position: "relative" }}>
                <Reveal>
                  <span
                    style={{
                      fontFamily: "var(--font-mono)",
                      fontSize: "0.85rem",
                      color: "var(--color-accent-mid)",
                      letterSpacing: "0.12em",
                      textTransform: "uppercase",
                      display: "block",
                      marginBottom: "1.1rem",
                    }}
                  >
                    // servizi
                  </span>
                  <h2
                    style={{
                      fontFamily: "var(--font-display)",
                      fontSize: "clamp(2rem, 4vw, 2.8rem)",
                      maxWidth: "28ch",
                      lineHeight: 1.15,
                    }}
                  >
                    Cosa posso{" "}
                    <span
                      style={{
                        fontWeight: 800,
                        background: "var(--gradient-text)",
                        WebkitBackgroundClip: "text",
                        WebkitTextFillColor: "transparent",
                        backgroundClip: "text",
                      }}
                    >
                      costruire per te
                    </span>
                  </h2>
                  <p
                    style={{
                      color: "var(--color-text-2)",
                      fontSize: "1.1rem",
                      maxWidth: "52ch",
                      lineHeight: 1.8,
                      marginTop: "1rem",
                      fontWeight: 300,
                    }}
                  >
                    Progetto e sviluppo prodotti digitali completi — dall'idea al
                    lancio. Ogni servizio è pensato per chi vuole risultati concreti,
                    non soluzioni generiche.
                  </p>
                </Reveal>

                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fit, minmax(290px, 1fr))",
                    gap: "1.5rem",
                    marginTop: "3.5rem",
                  }}
                >
                  {[
                    {
                      icon: Smartphone,
                      titolo: "App Mobile",
                      tagline: "iOS & Android in un unico codebase",
                      color: "#7c3aed",
                      deliverables: [
                        "React Native cross-platform",
                        "Autenticazione & profilo utente",
                        "Push notification & offline mode",
                        "Pubblicazione App Store / Play Store",
                      ],
                      stack: ["React Native", "Expo", "Firebase", "TypeScript"],
                    },
                    {
                      icon: Server,
                      titolo: "SaaS B2B",
                      tagline: "Piattaforme verticali pronte a scalare",
                      color: "#f472b6",
                      deliverables: [
                        "Dashboard multi-tenant",
                        "Gestione ruoli & permessi",
                        "Pagamenti ricorrenti (Stripe)",
                        "API REST documentate",
                      ],
                      stack: ["Node.js", "React", "Supabase", "Stripe"],
                    },
                    {
                      icon: Globe,
                      titolo: "Web App",
                      tagline: "Interfacce rapide e scalabili",
                      color: "#22d3ee",
                      deliverables: [
                        "SPA React ottimizzata SEO",
                        "Integrazione CMS o database",
                        "Analytics & tracciamento eventi",
                        "Deploy cloud con CI/CD",
                      ],
                      stack: ["React", "Node.js", "Firebase", "TypeScript"],
                    },
                    {
                      icon: MessageSquare,
                      titolo: "Consulenza Tecnica",
                      tagline: "Affiancamento strategico e code review",
                      color: "#a78bfa",
                      deliverables: [
                        "Architettura di sistema",
                        "Code review & refactoring",
                        "Scelta dello stack tecnologico",
                        "Roadmap prodotto & MVP planning",
                      ],
                      stack: ["Analisi", "Review", "Planning", "Mentoring"],
                    },
                  ].map(({ icon: Icon, titolo, tagline, color, deliverables, stack }, i) => (
                    <Reveal key={titolo} delay={i * 0.08}>
                      <div
                        onMouseEnter={(e) => {
                          e.currentTarget.style.border = `1px solid ${color}55`;
                          e.currentTarget.style.boxShadow = `0 24px 64px ${color}18, inset 0 1px 0 ${color}15`;
                          e.currentTarget.style.transform = "translateY(-6px)";
                          e.currentTarget.style.background = "rgba(255,255,255,0.05)";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.border = `1px solid ${color}28`;
                          e.currentTarget.style.boxShadow = "0 4px 24px rgba(0,0,0,0.2)";
                          e.currentTarget.style.transform = "translateY(0)";
                          e.currentTarget.style.background = "rgba(255,255,255,0.025)";
                        }}
                        style={{
                          background: "rgba(255,255,255,0.025)",
                          border: `1px solid ${color}28`,
                          backdropFilter: "blur(16px)",
                          borderRadius: "var(--radius-lg)",
                          padding: "2rem",
                          position: "relative",
                          overflow: "hidden",
                          transition: "all 0.32s cubic-bezier(0.4,0,0.2,1)",
                          boxShadow: "0 4px 24px rgba(0,0,0,0.2)",
                          display: "flex",
                          flexDirection: "column",
                          gap: "1.4rem",
                        }}
                      >
                        {/* Glow angolo */}
                        <div
                          style={{
                            position: "absolute",
                            top: 0,
                            right: 0,
                            width: 200,
                            height: 200,
                            background: `radial-gradient(circle at top right, ${color}18, transparent 65%)`,
                            pointerEvents: "none",
                          }}
                        />
                        {/* Accent line top */}
                        <div
                          style={{
                            position: "absolute",
                            top: 0,
                            left: 0,
                            right: 0,
                            height: "2px",
                            background: `linear-gradient(90deg, ${color}00, ${color}90, ${color}00)`,
                            pointerEvents: "none",
                          }}
                        />

                        {/* Header */}
                        <div style={{ display: "flex", alignItems: "flex-start", gap: "1rem" }}>
                          <div
                            style={{
                              width: 52,
                              height: 52,
                              borderRadius: "14px",
                              background: `${color}15`,
                              border: `1px solid ${color}35`,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              flexShrink: 0,
                              boxShadow: `0 0 20px ${color}25`,
                            }}
                          >
                            <Icon size={22} color={color} />
                          </div>
                          <div>
                            <h3
                              style={{
                                fontFamily: "var(--font-display)",
                                fontWeight: 700,
                                fontSize: "1.15rem",
                                marginBottom: "0.25rem",
                              }}
                            >
                              {titolo}
                            </h3>
                            <p
                              style={{
                                fontFamily: "var(--font-mono)",
                                fontSize: "0.78rem",
                                color: color,
                                opacity: 0.8,
                              }}
                            >
                              {tagline}
                            </p>
                          </div>
                        </div>

                        {/* Deliverables */}
                        <ul style={{ listStyle: "none", padding: 0, margin: 0, display: "flex", flexDirection: "column", gap: "0.55rem" }}>
                          {deliverables.map((item) => (
                            <li
                              key={item}
                              style={{
                                display: "flex",
                                alignItems: "flex-start",
                                gap: "0.6rem",
                                color: "var(--color-text-2)",
                                fontSize: "0.92rem",
                                lineHeight: 1.5,
                              }}
                            >
                              <CheckCircle2 size={15} color={color} style={{ flexShrink: 0, marginTop: "0.15rem", opacity: 0.85 }} />
                              {item}
                            </li>
                          ))}
                        </ul>

                        {/* Stack chips */}
                        <div style={{ display: "flex", flexWrap: "wrap", gap: "6px", marginTop: "auto" }}>
                          {stack.map((t) => (
                            <span
                              key={t}
                              style={{
                                fontFamily: "var(--font-mono)",
                                fontSize: "0.72rem",
                                color: color,
                                background: `${color}12`,
                                border: `1px solid ${color}30`,
                                borderRadius: "6px",
                                padding: "3px 10px",
                                letterSpacing: "0.04em",
                              }}
                            >
                              {t}
                            </span>
                          ))}
                        </div>
                      </div>
                    </Reveal>
                  ))}
                </div>

                {/* CTA */}
                <Reveal delay={0.3}>
                  <div
                    style={{
                      marginTop: "3rem",
                      display: "flex",
                      alignItems: "center",
                      gap: "1.5rem",
                      flexWrap: "wrap",
                    }}
                  >
                    <Link
                      to="/collabora"
                      className="btn-primary"
                      onClick={events.ctaCollabora}
                      style={{ display: "inline-flex", alignItems: "center", gap: "0.5rem" }}
                    >
                      Parliamo del tuo progetto
                      <ArrowRight size={18} />
                    </Link>
                    <Link to="/servizi" className="btn-secondary">
                      Pacchetti e prezzi
                    </Link>
                    <span
                      style={{
                        fontFamily: "var(--font-mono)",
                        fontSize: "0.82rem",
                        color: "var(--color-muted)",
                      }}
                    >
                      Prima call gratuita — rispondo entro 24h
                    </span>
                  </div>
                </Reveal>
              </div>
            </section>

            {/* ══════════ PROGETTI ══════════ */}
            <section
              className="section"
              style={{
                borderTop: "1px solid var(--color-border)",
                position: "relative",
                overflow: "hidden",
              }}
            >
              <div
                className="blob"
                style={{
                  width: 500,
                  height: 400,
                  background:
                    "radial-gradient(circle, rgba(124,58,237,0.14) 0%, transparent 70%)",
                  top: 0,
                  right: "-15%",
                  animationDuration: "18s",
                }}
              />
              <div
                className="container"
                style={{ position: "relative", zIndex: 1 }}
              >
                <Reveal>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "flex-end",
                      flexWrap: "wrap",
                      gap: "1rem",
                      marginBottom: "3rem",
                    }}
                  >
                    <div>
                      <span
                        style={{
                          fontFamily: "var(--font-mono)",
                          fontSize: "0.85rem",
                          color: "var(--color-accent-mid)",
                          letterSpacing: "0.12em",
                          textTransform: "uppercase",
                          display: "block",
                          marginBottom: "0.75rem",
                        }}
                      >
                        // progetti
                      </span>
                      <h2
                        style={{
                          fontFamily: "var(--font-display)",
                          fontSize: "clamp(2rem, 4vw, 2.8rem)",
                          lineHeight: 1.15,
                        }}
                      >
                        Cosa sto{" "}
                        <span
                          style={{
                            fontFamily: "var(--font-display)",
                            fontWeight: 800,
                            background: "var(--gradient-text)",
                            WebkitBackgroundClip: "text",
                            WebkitTextFillColor: "transparent",
                            backgroundClip: "text",
                          }}
                        >
                          costruendo
                        </span>
                      </h2>
                    </div>
                    <Link
                      to="/progetti"
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: "6px",
                        fontSize: "0.875rem",
                        color: "var(--color-muted)",
                        transition: "color 0.3s",
                        fontWeight: 500,
                      }}
                      onMouseEnter={(e) =>
                        (e.currentTarget.style.color = "var(--color-accent-lt)")
                      }
                      onMouseLeave={(e) =>
                        (e.currentTarget.style.color = "var(--color-muted)")
                      }
                    >
                      Tutti i progetti <ArrowRight size={13} />
                    </Link>
                  </div>
                </Reveal>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
                    gap: "1.25rem",
                  }}
                >
                  {PROGETTI.map((p, i) => (
                    <ProjectCard key={p.slug} p={p} delay={i * 0.1} />
                  ))}
                </div>
              </div>
            </section>

            {/* ══════════ NUMERI ══════════ */}
            <section
              className="section"
              style={{ borderTop: "1px solid var(--color-border)" }}
            >
              <div className="container">
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
                    gap: "1.25rem",
                  }}
                >
                  {[
                    {
                      valore: "4+",
                      label: "Progetti lanciati",
                      icon: Layers,
                      color: "#7c3aed",
                    },
                    {
                      valore: "5+",
                      label: "Anni di esperienza",
                      icon: Code2,
                      color: "#f472b6",
                    },
                    {
                      valore: "10+",
                      label: "Tecnologie padroneggiate",
                      icon: Zap,
                      color: "#22d3ee",
                    },
                  ].map(({ valore, label, icon: Icon, color }, i) => (
                    <Reveal key={label} delay={i * 0.12}>
                      <div
                        style={{
                          background: "rgba(255,255,255,0.025)",
                          border: `1px solid ${color}22`,
                          backdropFilter: "blur(16px)",
                          borderRadius: "var(--radius-lg)",
                          padding: "2.5rem 2rem",
                          textAlign: "center",
                          position: "relative",
                          overflow: "hidden",
                        }}
                      >
                        {/* Glow radiale centrato */}
                        <div
                          style={{
                            position: "absolute",
                            top: "50%",
                            left: "50%",
                            transform: "translate(-50%, -50%)",
                            width: 240,
                            height: 240,
                            background: `radial-gradient(circle, ${color}14 0%, transparent 65%)`,
                            pointerEvents: "none",
                          }}
                        />
                        {/* Top accent line */}
                        <div
                          style={{
                            position: "absolute",
                            top: 0,
                            left: 0,
                            right: 0,
                            height: "2px",
                            background: `linear-gradient(90deg, transparent, ${color}80, transparent)`,
                            pointerEvents: "none",
                          }}
                        />
                        {/* Icona cerchio */}
                        <div
                          style={{
                            width: 52,
                            height: 52,
                            borderRadius: "50%",
                            background: `${color}12`,
                            border: `1px solid ${color}30`,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            margin: "0 auto 1.5rem",
                            boxShadow: `0 0 24px ${color}25`,
                            position: "relative",
                            zIndex: 1,
                          }}
                        >
                          <Icon size={22} color={color} />
                        </div>
                        {/* Numero */}
                        <div
                          style={{
                            fontFamily: "var(--font-display)",
                            fontWeight: 900,
                            fontSize: "clamp(3rem, 6vw, 4.5rem)",
                            background: `linear-gradient(135deg, ${color} 0%, white 100%)`,
                            WebkitBackgroundClip: "text",
                            WebkitTextFillColor: "transparent",
                            backgroundClip: "text",
                            lineHeight: 1,
                            marginBottom: "0.75rem",
                            letterSpacing: "-0.04em",
                            position: "relative",
                            zIndex: 1,
                          }}
                        >
                          {valore}
                        </div>
                        <p
                          style={{
                            fontSize: "1rem",
                            color: "var(--color-muted)",
                            fontWeight: 500,
                            position: "relative",
                            zIndex: 1,
                          }}
                        >
                          {label}
                        </p>
                      </div>
                    </Reveal>
                  ))}
                </div>
              </div>
            </section>

            {/* ══════════ CTA FINALE ══════════ */}
            <section
              className="section"
              style={{
                borderTop: "1px solid var(--color-border)",
                position: "relative",
                overflow: "hidden",
              }}
            >
              <div
                className="blob"
                style={{
                  width: 800,
                  height: 500,
                  background:
                    "radial-gradient(circle, rgba(124,58,237,0.18) 0%, rgba(244,114,182,0.10) 50%, transparent 70%)",
                  top: "-20%",
                  left: "50%",
                  transform: "translateX(-50%)",
                  animationDuration: "22s",
                }}
              />
              <div
                className="container"
                style={{ position: "relative", zIndex: 1 }}
              >
                <Reveal>
                  {/* Wrapper gradient border */}
                  <div
                    style={{
                      background:
                        "linear-gradient(135deg, rgba(124,58,237,0.55) 0%, rgba(167,139,250,0.35) 40%, rgba(244,114,182,0.55) 100%)",
                      borderRadius: "calc(var(--radius-xl) + 2px)",
                      padding: "2px",
                      boxShadow:
                        "0 0 100px rgba(124,58,237,0.18), 0 0 40px rgba(244,114,182,0.10)",
                    }}
                  >
                    <div
                      style={{
                        background: "rgba(7,7,14,0.96)",
                        backdropFilter: "blur(32px)",
                        WebkitBackdropFilter: "blur(32px)",
                        borderRadius: "var(--radius-xl)",
                        padding: "clamp(3rem, 7vw, 6rem)",
                        textAlign: "center",
                        position: "relative",
                        overflow: "hidden",
                      }}
                    >
                      {/* Glow superiore */}
                      <div
                        style={{
                          position: "absolute",
                          top: 0,
                          left: "50%",
                          transform: "translateX(-50%)",
                          width: "70%",
                          height: "250px",
                          background:
                            "radial-gradient(ellipse at 50% 0%, rgba(124,58,237,0.22) 0%, transparent 70%)",
                          pointerEvents: "none",
                        }}
                      />
                      {/* Glow inferiore */}
                      <div
                        style={{
                          position: "absolute",
                          bottom: 0,
                          left: "50%",
                          transform: "translateX(-50%)",
                          width: "60%",
                          height: "180px",
                          background:
                            "radial-gradient(ellipse at 50% 100%, rgba(244,114,182,0.12) 0%, transparent 70%)",
                          pointerEvents: "none",
                        }}
                      />

                      <span
                        style={{
                          fontFamily: "var(--font-mono)",
                          fontSize: "0.85rem",
                          color: "var(--color-accent-mid)",
                          letterSpacing: "0.12em",
                          textTransform: "uppercase",
                          display: "block",
                          marginBottom: "1.25rem",
                          position: "relative",
                          zIndex: 1,
                        }}
                      >
                        // lavoriamo insieme
                      </span>
                      <h2
                        style={{
                          fontFamily: "var(--font-display)",
                          fontSize: "clamp(2.2rem, 5vw, 3.5rem)",
                          maxWidth: "20ch",
                          margin: "0 auto 0.5rem",
                          lineHeight: 1.15,
                          letterSpacing: "-0.03em",
                          position: "relative",
                          zIndex: 1,
                        }}
                      >
                        Hai un'idea?{" "}
                        <span
                          style={{
                            fontFamily: "var(--font-display)",
                            fontWeight: 800,
                            background: "var(--gradient-text)",
                            WebkitBackgroundClip: "text",
                            WebkitTextFillColor: "transparent",
                            backgroundClip: "text",
                          }}
                        >
                          Costruiamola insieme.
                        </span>
                      </h2>
                      <p
                        style={{
                          color: "var(--color-text-2)",
                          fontSize: "1.15rem",
                          fontWeight: 300,
                          maxWidth: "46ch",
                          margin: "1.5rem auto 3rem",
                          lineHeight: 1.8,
                          position: "relative",
                          zIndex: 1,
                        }}
                      >
                        Sviluppo web, app mobile, consulenza tecnica. Ti
                        rispondo entro 48 ore.
                      </p>
                      <div
                        style={{
                          display: "flex",
                          gap: "1rem",
                          justifyContent: "center",
                          flexWrap: "wrap",
                          position: "relative",
                          zIndex: 1,
                        }}
                      >
                        <Link
                          to="/collabora"
                          className="btn-primary"
                          style={{ padding: "14px 36px", fontSize: "1rem" }}
                          onClick={events.ctaCollabora}
                        >
                          Iniziamo <ArrowRight size={16} />
                        </Link>
                        <a
                          href="mailto:vmdpproject@gmail.com"
                          className="btn-secondary"
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: "8px",
                            padding: "14px 36px",
                            fontSize: "1rem",
                          }}
                        >
                          Scrivi una mail <ExternalLink size={15} />
                        </a>
                      </div>
                    </div>
                  </div>
                </Reveal>
              </div>
            </section>

            {/* ─── Footer ─── */}
            <footer
              style={{
                borderTop: "1px solid var(--color-border)",
                padding: "2rem 0",
              }}
            >
              <div
                className="container"
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  flexWrap: "wrap",
                  gap: "1rem",
                }}
              >
                <span
                  style={{
                    fontFamily: "var(--font-display)",
                    fontWeight: 700,
                    fontSize: "0.95rem",
                    letterSpacing: "-0.02em",
                  }}
                >
                  VMDProject
                </span>
                <div style={{ display: "flex", gap: "1.5rem" }}>
                  {[
                    { label: "Privacy", href: "/policy" },
                    { label: "Termini", href: "/terms-and-conditions" },
                  ].map(({ label, href }) => (
                    <Link
                      key={href}
                      to={href}
                      style={{
                        fontSize: "0.9rem",
                        color: "var(--color-muted)",
                        transition: "color 0.3s",
                      }}
                      onMouseEnter={(e) =>
                        (e.target.style.color = "var(--color-text)")
                      }
                      onMouseLeave={(e) =>
                        (e.target.style.color = "var(--color-muted)")
                      }
                    >
                      {label}
                    </Link>
                  ))}
                </div>
                <span
                  style={{
                    fontFamily: "var(--font-mono)",
                    fontSize: "0.85rem",
                    color: "var(--color-muted)",
                  }}
                >
                  © {new Date().getFullYear()} VMDProject · P.IVA 10990261215
                </span>
              </div>
            </footer>
          </motion.main>
        </>
      )}

      <style>{`
        @keyframes dotPulse {
          0%, 100% { box-shadow: 0 0 6px #34d399;  opacity: 1; }
          50%       { box-shadow: 0 0 14px #34d399; opacity: 0.6; }
        }
        @keyframes orbFloat {
          0%   { transform: translate(-50%, -50%) translateY(0px); }
          100% { transform: translate(-50%, -50%) translateY(-22px); }
        }
        @keyframes orbSpin {
          from { filter: hue-rotate(0deg)   blur(0px); }
          to   { filter: hue-rotate(40deg)  blur(0px); }
        }
      `}</style>
    </>
  );
}
